import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DroppableStateSnapshot,
  DropResult,
} from "@hello-pangea/dnd";
import {typedMemo} from "../../../../utils/typescript";
import {useCallback, useMemo} from "react";
import {constrainVerticalAxis} from "../../../../utils/dnd";
import {Box, HStack, Icon, Stack} from "@chakra-ui/react";
import {Bars3Icon} from "@heroicons/react/16/solid";
import {Checkbox} from "../../../ui/checkbox";
import {
  Column,
  ColumnId,
  ExtractTableContext,
  isColumnHidden,
  isColumnRenderable,
  SetColumnPositionFunction,
  SetColumnVisibleFunction,
  TableConfig,
} from "../../../../hooks/table";

export const ColumnEditor = typedMemo(
  <C extends TableConfig>({
    config,
    context,
    columnOrder,
    visibleColumns,
    setColumnPosition,
    setColumnVisible,
  }: {
    config: C;
    context: ExtractTableContext<C>;
    columnOrder: ColumnId<C>[];
    visibleColumns: Set<ColumnId<C>>;
    setColumnPosition: SetColumnPositionFunction<C>;
    setColumnVisible: SetColumnVisibleFunction<C>;
  }) => {
    const columns = useMemo(
      () =>
        columnOrder
          .filter(
            columnId =>
              isColumnRenderable(config.columns[columnId]) && !isColumnHidden(config.columns[columnId], context),
          )
          .map(columnId => [columnId, config.columns[columnId]] as const),
      [columnOrder, config.columns, context],
    );

    const onDragEnd = useCallback(
      (result: DropResult) => {
        // If we dropped outside the list, do nothing
        if (result.destination == null) return;

        setColumnPosition(result.draggableId, result.destination.index);
      },
      [setColumnPosition],
    );

    return (
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable
          droppableId="display-columns"
          renderClone={(provided, snapshot, rubric) => {
            const [columnId, column] = columns[rubric.source.index];
            constrainVerticalAxis(provided, snapshot);

            return (
              <ColumnRow
                columnId={columnId}
                column={column}
                visibleColumns={visibleColumns}
                setColumnVisible={setColumnVisible}
                provided={provided}
                snapshot={snapshot}
              />
            );
          }}
        >
          {(provided, droppableSnapshot) => (
            <Stack gap={0} {...provided.droppableProps} ref={provided.innerRef}>
              {columns.map(([columnId, column], idx) => (
                <Draggable key={columnId} draggableId={columnId} index={idx}>
                  {(provided, snapshot) => (
                    <ColumnRow
                      columnId={columnId}
                      column={column}
                      visibleColumns={visibleColumns}
                      setColumnVisible={setColumnVisible}
                      provided={provided}
                      snapshot={snapshot}
                      droppableSnapshot={droppableSnapshot}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>
    );
  },
);

const ColumnRow = typedMemo(
  <C extends TableConfig>({
    columnId,
    column,
    visibleColumns,
    setColumnVisible,
    provided,
    snapshot,
    droppableSnapshot,
  }: {
    columnId: string;
    column: Column;
    visibleColumns: Set<ColumnId<C>>;
    setColumnVisible: SetColumnVisibleFunction<C>;
    provided: DraggableProvided;
    snapshot: DraggableStateSnapshot;
    droppableSnapshot?: DroppableStateSnapshot;
  }) => {
    return (
      <HStack
        gap={0}
        // Only show hover effects if we're not being dragged over
        _hover={droppableSnapshot?.isDraggingOver ? undefined : {bg: "bg.muted"}}
        // Force the hover color if we're being dragged
        bg={snapshot.isDragging ? "bg.muted" : undefined}
        borderRadius="sm"
        ref={provided.innerRef}
        alignItems="stretch"
        {...provided.draggableProps}
      >
        <Box {...provided.dragHandleProps} py={1} px={3}>
          <Icon boxSize="1em" color="fg.muted">
            <Bars3Icon />
          </Icon>
        </Box>
        <Checkbox
          flex="1 1 auto"
          colorPalette="blue"
          size="sm"
          key={columnId}
          checked={visibleColumns.has(columnId)}
          onCheckedChange={d => setColumnVisible(columnId, !!d.checked)}
          disabled={column.forceVisible}
        >
          {column.label}
        </Checkbox>
      </HStack>
    );
  },
);
